/**
 * CurrentTimeIndicator
 * Red horizontal line showing the current time on today's column.
 * Updates every 60 seconds.
 */
import { useState, useEffect } from "react";

interface CurrentTimeIndicatorProps {
  /** Hour the time grid starts (e.g. 8 for 08:00) */
  startHour: number;
  /** Hour the time grid ends (e.g. 18 for 18:00) */
  endHour: number;
}

export function CurrentTimeIndicator({
  startHour,
  endHour,
}: CurrentTimeIndicatorProps) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const hours = now.getHours();
  const minutes = now.getMinutes();
  const totalMinutes = hours * 60 + minutes;
  const gridStart = startHour * 60;
  const gridEnd = endHour * 60;

  // Only show when the current time falls within the visible range
  if (totalMinutes < gridStart || totalMinutes > gridEnd) {
    return null;
  }

  const pct = ((totalMinutes - gridStart) / (gridEnd - gridStart)) * 100;

  return (
    <div
      className="pointer-events-none absolute left-0 right-0 z-20 flex items-center"
      style={{ top: `${pct}%` }}
      data-testid="current-time-indicator"
    >
      <div className="h-2.5 w-2.5 -ml-1.5 rounded-full bg-red-500" />
      <div className="h-px flex-1 bg-red-500" />
    </div>
  );
}
